import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FileImage } from '../../components/ui/ApiImage';
import { ClipboardImageIcon } from '../../components/ui/icons';
import { uploadFile } from '../locations/api';
import {
  attachPackageImage,
  detachPackageImage,
  listPackageImages,
  type Mount,
  type Package,
  updatePackage,
} from './api';

export function PackageEditor({ package: row, onClose }: { package: Package; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(row.name_ru ?? '');
  const [size, setSize] = useState(row.size_mm ?? '');
  const [mount, setMount] = useState<Mount>(row.mount);
  const [description, setDescription] = useState(row.description ?? '');
  const [url, setUrl] = useState(row.datasheet_url ?? '');
  const images = useQuery({ queryKey: ['reference', 'packages', row.id, 'images'], queryFn: () => listPackageImages(row.id) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['reference'] });
  const save = useMutation({
    mutationFn: () => updatePackage(row.id, { name_ru: name || null, size_mm: size || null, mount, description: description || null, datasheet_url: url || null }),
    onSuccess: () => { refresh(); onClose(); },
  });
  const upload = useMutation({
    mutationFn: async (files: File[]) => { for (const file of files) { const stored = await uploadFile(file); await attachPackageImage(row.id, stored.id); } },
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => detachPackageImage(row.id, id), onSuccess: refresh });

  const paste = async () => {
    const items = await navigator.clipboard.read();
    const files = await Promise.all(
      items.flatMap((item) =>
        item.types
          .filter((type) => type.startsWith('image/'))
          .map(async (type) => new File([await item.getType(type)], 'clipboard-image.png', { type })),
      ),
    );
    if (files.length) upload.mutate(files);
  };

  return <section className="panel package-editor">
    <header className="panel__section-head"><h3>{t('packages.editorTitle', { code: row.code })}</h3></header>
    <div className="component-form__row">
      <label>{t('packages.name')}<input value={name} onChange={(event) => setName(event.target.value)} /></label>
      <label>{t('components.sizeMm')}<input value={size} onChange={(event) => setSize(event.target.value)} /></label>
      <label>{t('components.mount.label')}<select value={mount} onChange={(event) => setMount(event.target.value as Mount)}><option value="other">{t('components.mount.other')}</option><option value="smd">{t('components.mount.smd')}</option><option value="tht">{t('components.mount.tht')}</option></select></label>
    </div>
    <label>{t('packages.documentation')}<input value={url} type="url" onChange={(event) => setUrl(event.target.value)} /></label>
    <label>{t('packages.description')}<textarea rows={5} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
    <header className="gallery__head"><h3>{t('packages.images')}</h3><button type="button" className="gallery__add" onClick={() => input.current?.click()} disabled={upload.isPending}>+</button><button type="button" className="gallery__add" onClick={() => void paste()} disabled={upload.isPending} title={t('components.pasteImage')} aria-label={t('components.pasteImage')}><ClipboardImageIcon /></button></header>
    <input ref={input} type="file" accept="image/*" multiple hidden onChange={(event) => { const files = Array.from(event.target.files ?? []); if (files.length) upload.mutate(files); event.target.value = ''; }} />
    <ul className="gallery__grid">{(images.data ?? []).map((image) => <li key={image.id}><FileImage fileId={image.file_id} variant="thumb" alt={image.caption ?? row.code} /><button type="button" onClick={() => remove.mutate(image.id)}>{t('components.removePhoto')}</button></li>)}</ul>
    <div className="component-form__actions"><button type="button" onClick={() => save.mutate()} disabled={save.isPending}>{t('common.save')}</button><button type="button" onClick={onClose}>{t('common.cancel')}</button></div>
  </section>;
}
